import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Phone, Target, TrendingUp, DollarSign, Clock, CheckCircle } from "lucide-react";
import { useState } from "react";

export default function AgentMetrics() {
  const [selectedPlacement, setSelectedPlacement] = useState<string>("all");
  
  const { data: placements } = trpc.placements.list.useQuery();
  const { data: metrics, isLoading } = trpc.agent.metrics.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );
  const { data: outcomes } = trpc.agent.callOutcomes.useQuery(
    selectedPlacement !== "all" ? { placementId: parseInt(selectedPlacement) } : undefined
  );

  const formatPercent = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0%";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US").format(num);
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "$0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
  };

  const outcomeData = Array.isArray(outcomes) ? outcomes : [];
  const placementData = Array.isArray(placements) ? placements : [];

  const outcomeColors: Record<string, string> = {
    'no_answer': 'bg-gray-500',
    'voicemail': 'bg-blue-500',
    'wrong_number': 'bg-red-500',
    'right_party_contact': 'bg-emerald-500',
    'promise_to_pay': 'bg-amber-500',
    'settlement_accepted': 'bg-green-500',
    'payment_plan_setup': 'bg-purple-500',
    'refused': 'bg-red-400',
    'callback_scheduled': 'bg-cyan-500',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">AI Agent Performance</h1>
            <p className="text-muted-foreground">Real-time metrics from automated collection calls</p>
          </div>
          <Select value={selectedPlacement} onValueChange={setSelectedPlacement}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Select Placement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Placements</SelectItem>
              {placementData.map((p: any) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.placement_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(metrics?.total_calls)}</div>
              <p className="text-xs text-muted-foreground">
                Avg duration: {Math.round(Number(metrics?.avg_call_duration || 0))}s
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">RPC Rate</CardTitle>
              <Target className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">{formatPercent(metrics?.rpc_rate)}</div>
              <p className="text-xs text-muted-foreground">
                {formatNumber(metrics?.rpc_count)} right party contacts
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">PTP Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-500">{formatPercent(metrics?.ptp_rate)}</div>
              <p className="text-xs text-muted-foreground">
                {formatNumber(metrics?.ptp_count)} promises to pay
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Settlements</CardTitle>
              <CheckCircle className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-500">{formatNumber(metrics?.settlements_accepted)}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(metrics?.total_settlement_value)} value
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Call Outcome Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Call Outcome Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {outcomeData.map((outcome: any) => {
                const total = outcomeData.reduce((sum: number, o: any) => sum + Number(o.count), 0);
                const percentage = total > 0 ? (Number(outcome.count) / total) * 100 : 0;
                return (
                  <div key={outcome.call_outcome} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{outcome.call_outcome.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{formatNumber(outcome.count)} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className={`h-full ${outcomeColors[outcome.call_outcome] || 'bg-gray-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Performance Insights */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Conversion Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <span>Total Calls</span>
                  <span className="font-bold">{formatNumber(metrics?.total_calls)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10">
                  <span>Right Party Contacts</span>
                  <span className="font-bold text-blue-500">{formatNumber(metrics?.rpc_count)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10">
                  <span>Promises to Pay</span>
                  <span className="font-bold text-emerald-500">{formatNumber(metrics?.ptp_count)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10">
                  <span>Settlements Accepted</span>
                  <span className="font-bold text-amber-500">{formatNumber(metrics?.settlements_accepted)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Settlement Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center p-6 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                  <p className="text-3xl font-bold text-emerald-500">{formatCurrency(metrics?.total_settlement_value)}</p>
                  <p className="text-sm text-muted-foreground mt-1">Total Settlement Value</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <p className="text-xl font-bold">{formatNumber(metrics?.settlements_accepted)}</p>
                    <p className="text-xs text-muted-foreground">Settlements</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <p className="text-xl font-bold">
                      {formatCurrency(Number(metrics?.total_settlement_value || 0) / Math.max(Number(metrics?.settlements_accepted || 1), 1))}
                    </p>
                    <p className="text-xs text-muted-foreground">Avg Settlement</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
